import { vec3 } from 'gl-matrix';
import { FreeViewCamera } from 'noodle/engine/camera';
import { Engine } from 'noodle/engine/Engine';
import { toRadian } from 'noodle/engine/math';

type ActionList = { [key: string]: () => void };

/**
 * Live tunable parameters for the free-view camera. Returned alongside the
 * action set so the dev UI can drive `moveSpeed` for large open-world scenes
 * (the keyboard accelerator is too slow to reach useful speeds for km-scale
 * terrains).
 */
export interface CameraControls {
  moveSpeed: number;
}

export interface CameraActionBundle {
  actions: ActionList;
  controls: CameraControls;
}

const MIN_MOVE_SPEED = 0.05;
const MAX_MOVE_SPEED = 50;

export function createCameraActions(engine: Engine, camera: FreeViewCamera): CameraActionBundle {
  const controls: CameraControls = { moveSpeed: 0.5 };
  const lookSpeed = 2;
  const rotationSpeed = 2;
  const actions: ActionList = {};

  actions['Movement.GoUp'] = () => {
    camera.y += controls.moveSpeed;
  };

  actions['Movement.GoDown'] = () => {
    camera.y -= controls.moveSpeed;
  };

  actions['Movement.Accelerate'] = () => {
    controls.moveSpeed = Math.min(MAX_MOVE_SPEED, controls.moveSpeed + 0.05);
  };

  actions['Movement.Decelerate'] = () => {
    controls.moveSpeed = Math.max(MIN_MOVE_SPEED, controls.moveSpeed - 0.05);
  };

  actions['Movement.Forward'] = () => {
    camera.x -= Math.sin(toRadian(camera.rotation)) * controls.moveSpeed;
    camera.z -= Math.cos(toRadian(camera.rotation)) * controls.moveSpeed;
  };
  actions['Movement.Backward'] = () => {
    camera.x += Math.sin(toRadian(camera.rotation)) * controls.moveSpeed;
    camera.z += Math.cos(toRadian(camera.rotation)) * controls.moveSpeed;
  };
  actions['Movement.Left'] = () => {
    camera.rotation -= rotationSpeed;
  };
  actions['Movement.Right'] = () => {
    camera.rotation += rotationSpeed;
  };
  actions['Movement.LookUp'] = () => {
    camera.tilt += lookSpeed;
  };
  actions['Movement.LookDown'] = () => {
    camera.tilt -= lookSpeed;
  };
  actions['Object.Rotate'] = () => {};

  let lightValue = 0.6;
  actions['Light.ToggleAmbient'] = function () {
    lightValue += 0.01;
    if (lightValue > 1) {
      lightValue = 0.0;
    }
    engine.activeScene!.lightManager.ambientColor = vec3.fromValues(
      lightValue,
      lightValue,
      lightValue,
    );
  };

  return { actions, controls };
}

export function createPlayerActions(engine: Engine): ActionList {
  // In player mode, movement is handled by PlayerControlBehaviour reading key state.
  // Only non-movement actions are active here.
  let actions: ActionList = {};

  var lightValue = 0.6;
  actions['Light.ToggleAmbient'] = function () {
    lightValue += 0.01;
    if (lightValue > 1) {
      lightValue = 0.0;
    }
    engine.activeScene!.lightManager.ambientColor = vec3.fromValues(
      lightValue,
      lightValue,
      lightValue,
    );
  };

  return actions;
}
